import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UploadLimits } from '@ai-content-hub/shared-config';

import { systemSettingsService } from '@/services/systemSettings.service';
import { systemSettingsKeys } from './systemSettingsKeys';
import { toUploadLimits } from './uploadLimitsMapping';

/**
 * Every runtime-config key with its provenance. Callers pass `enabled` = platform
 * staff: the endpoint needs `config.read`, and no other role may even ask.
 */
export function useSystemConfigQuery(
    enabled: boolean,
    userId: string | undefined,
    language: string
) {
    return useQuery({
        queryKey: systemSettingsKeys.config(userId, language),
        queryFn: () => systemSettingsService.getConfig(),
        enabled,
    });
}

const UPLOAD_LIMITS_STALE_MS = 30 * 60 * 1000;

/**
 * EAA-1212: the configured upload limits, for any signed-in user. Long stale time —
 * an admin's change reaching an open editor within half an hour is fine, and the
 * server enforces the limits regardless. `null` while unknown, so every upload site
 * keeps its own constant until the answer arrives (or for good, on error).
 */
export function useUploadLimitsQuery(enabled: boolean): UploadLimits | null {
    const query = useQuery({
        queryKey: systemSettingsKeys.uploadLimits(),
        queryFn: () => systemSettingsService.getUploadLimits(),
        enabled,
        staleTime: UPLOAD_LIMITS_STALE_MS,
        retry: 1,
    });
    return useMemo(() => toUploadLimits(query.data), [query.data]);
}

/**
 * EAA-1212: the acting tenant's storage-api space, for the read-only Storage section.
 * Scoped read (CLAUDE.md): the tenant id is in the key and the query stays disabled
 * until a tenant is picked — "All tenants" has no single space to show, and asking
 * without a tenant would be a different question, not an early answer. Only platform
 * staff may ask (`allowed` = the page gate).
 */
export function useStorageSpaceQuery(allowed: boolean, tenantId: string | null) {
    return useQuery({
        queryKey: systemSettingsKeys.storageSpace(tenantId),
        queryFn: () => systemSettingsService.getStorageSpace(tenantId as string),
        enabled: allowed && Boolean(tenantId),
        retry: 1,
    });
}
